import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin, Lock, Wifi, Smartphone, Key, Car, Users, Dumbbell, Sun, Waves, Gift, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const allMembershipAmenities = [
  { text: "LOCKERS (BYO LOCK)", icon: Lock },
  { text: "SHOWERS", icon: Waves },
  { text: "FREE WIFI", icon: Wifi },
  { text: "MOBILE APP", icon: Smartphone },
  { text: "KEYTAG (UPON REQUEST)", icon: Key },
  { text: "ACCESSIBLE PARKING LOTS", icon: Car }
];

const platinumFeatures = [
  { text: "UNLIMITED ACCESS TO ALL JERSEY GIRLS LOCATIONS", icon: MapPin },
  { text: "1 GUEST PER DAY", icon: Users },
  { text: "TANNING PRIVILEGES", icon: Sun },
  { text: "MASSAGE CHAIR PRIVILEGES", icon: Dumbbell },
  { text: "POOL PRIVILEGES (VOORHEES LOCATION ONLY)", icon: Waves },
];

const basicBenefits = [
  "Access to one selected Jersey Girls location",
];

const platinumBenefits = [
  "Unlimited access to all Jersey Girls locations",
  "1 guest per day",
  "Tanning privileges",
  "Massage chair privileges",
  "Pool privileges (Voorhees location only)",
];

function MembershipCard({ title, price, enrollmentFee, firstMonthNote, benefits, isPlatinum, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className={`relative ${isPlatinum ? 'bg-gradient-to-br from-brand-pink/10 to-brand-pink/5 border-2 border-brand-pink' : 'bg-white border-2 border-gray-300'} rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow`}
    >
      <div className="text-center">
        <h3 className={`text-xl md:text-2xl font-heading font-black ${isPlatinum ? 'text-brand-pink' : 'text-gray-900'} mb-6`}>
          Jersey Girls {title} Membership – No Commitment
        </h3>
        
        <div className="mb-6">
          <div className="flex items-baseline justify-center gap-2 mb-2">
            <span className="text-2xl text-gray-600">$</span>
            <span className={`text-5xl md:text-6xl font-heading font-black ${isPlatinum ? 'text-brand-pink' : 'text-gray-900'}`}>
              {price}
            </span>
            <span className="text-xl text-gray-600">/month</span>
          </div>
        </div>

        <div className={`border-t ${isPlatinum ? 'border-brand-pink/30' : 'border-gray-200'} pt-6 mb-6`}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Gift className={`w-5 h-5 ${isPlatinum ? 'text-brand-pink' : 'text-gray-600'}`} />
            <span className="text-sm text-gray-600">Enrollment Fee</span>
          </div>
          <p className={`text-2xl font-heading font-black ${isPlatinum ? 'text-brand-pink' : 'text-gray-900'}`}>
            ${enrollmentFee}
          </p>
          <p className="text-sm text-gray-600 mt-3">{firstMonthNote}</p>
        </div>

        <div className={`border-t ${isPlatinum ? 'border-brand-pink/30' : 'border-gray-200'} pt-6 mb-6 text-left`}>
          <p className="text-sm font-semibold text-gray-800 mb-3 text-center">Benefits of this membership option:</p>
          <ul className="space-y-2">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPlatinum ? 'text-brand-pink' : 'text-gray-600'}`} />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <Link to="/locations">
          <Button 
            size="lg" 
            className={`w-full ${isPlatinum ? 'bg-brand-pink hover:bg-brand-pink-hover text-white shadow-lg' : 'bg-gray-900 hover:bg-gray-800 text-white'} font-bold uppercase tracking-wider`}
          >
            <MapPin className="w-5 h-5 mr-2" />
            Choose Location
          </Button>
        </Link>
      </div>
    </motion.div>
  );
}

export default function MembershipsPage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Gym Membership",
    "provider": {
      "@type": "HealthClub",
      "name": "Jersey Girls"
    },
    "areaServed": {
      "@type": "State",
      "name": ["New Jersey", "Pennsylvania"]
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Basic Membership",
        "price": "19.99",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "19.99",
          "priceCurrency": "USD",
          "billingIncrement": "P1M"
        }
      },
      {
        "@type": "Offer",
        "name": "Platinum Membership",
        "price": "29.99",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "29.99",
          "priceCurrency": "USD",
          "billingIncrement": "P1M"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-white to-gray-50" data-testid="memberships-page">
      <SEO
        title="Membership Plans"
        description="Choose the Jersey Girls membership that fits your lifestyle. Basic and Platinum no-commitment memberships available."
        url="/memberships"
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 text-center bg-gradient-to-b from-gray-50 to-white">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-5xl md:text-7xl font-heading font-black text-gray-900 mb-6" data-testid="memberships-heading">
            MEMBERSHIPS
          </h1>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-20 space-y-16">
        {/* All Jersey Girls memberships include these amenities: */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="card-light no-hover p-8 md:p-10"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-black text-gray-900 mb-2">
              ALL JERSEY GIRLS MEMBERSHIPS PROVIDE YOU WITH ACCESS TO YOUR FAVORITE FITNESS CENTER
            </h2>
            <p className="text-gray-700 text-lg">and include amenities:</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {allMembershipAmenities.map((amenity, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200"
              >
                <div className="w-12 h-12 rounded-lg bg-brand-pink/10 flex items-center justify-center flex-shrink-0">
                  <amenity.icon className="w-6 h-6 text-brand-pink" />
                </div>
                <span className="text-gray-800 font-semibold text-sm">{amenity.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* UNLOCK PREMIUM ACCESS WITH OUR PLATINUM MEMBERSHIP */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white border border-gray-200 rounded-2xl p-8 md:p-10"
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-heading font-black text-gray-900 mb-4">
              UNLOCK PREMIUM ACCESS WITH OUR PLATINUM MEMBERSHIP:
            </h2>
          </div>
          <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-4">
            {platinumFeatures.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <feature.icon className="w-5 h-5 text-brand-pink" />
                <span className="text-gray-900 text-sm font-semibold">{feature.text}</span>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center py-8"
        >
          <p className="text-lg text-gray-800 font-heading font-black mb-2">Not fully commited?</p>
          <p className="text-gray-700">
            We have flexible agreements that can be cancelled with just 60 days notice.
          </p>
        </motion.section>

        {/* Membership Plans */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white border-2 border-gray-200 rounded-2xl p-8 md:p-10"
        >
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
              Membership rates:
            </h2>
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">
              Upon joining, all new members pay the initiation fee and their first month&apos;s dues.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <MembershipCard
              title="Basic"
              price="19.99"
              enrollmentFee="59"
              firstMonthNote="First month dues paid upfront today"
              benefits={basicBenefits}
              isPlatinum={false}
              delay={0.1}
            />
            <MembershipCard
              title="Platinum"
              price="29.99"
              enrollmentFee="1.00"
              firstMonthNote="First month dues paid today"
              benefits={platinumBenefits}
              isPlatinum={true}
              delay={0.2}
            />
            <MembershipCard
              title="Basic"
              price="14.99"
              enrollmentFee="149"
              firstMonthNote="First month dues paid upfront today"
              benefits={basicBenefits}
              isPlatinum={false}
              delay={0.3}
            />
          </div>

          {/* Terms Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6 space-y-2"
          >
            <div className="flex items-start gap-3">
              <Info className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700 space-y-2">
                <p>$49 recurring annual fee is due 45 days from join date.</p>
                <p>These memberships require a 60 day notice to cancel.</p>
                <p>Prices do not include tax.</p>
              </div>
            </div>
          </motion.div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center py-8"
        >
          <p className="text-gray-700 text-lg mb-2">
            Still not sure?{" "}
            <Link to="/free-pass" className="text-brand-pink font-bold underline hover:no-underline">
              Try a 1 day free pass
            </Link>
          </p>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center py-12"
        >
          <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-8">
            READY? FIND YOUR LOCATION
          </h2>
          <Link to="/locations">
            <Button size="lg" className="bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase text-xl px-12 py-8 shadow-xl transform hover:scale-105 transition-transform">
              <MapPin className="w-7 h-7 mr-3" />
              Find Your Fitness Center
            </Button>
          </Link>
        </motion.section>
      </div>
    </div>
  );
}
